import type { ReactNode } from "react";
import { Button, Filter, Icon, InputText, RigoFilters } from "@/deps/ui-library";
import {
  PAGE_SIZES,
  clearFilters,
  hasActiveFilters,
  isCustomRange,
  type ListSearch,
} from "./listQuery";

export interface SelectOption {
  value: string;
  label: string;
}

export interface ListFilterBarProps {
  search: ListSearch;
  onChange: (next: ListSearch) => void;
  searchPlaceholder: string;
  fiscalYears: SelectOption[];
  months: SelectOption[];
  /** Status and payment are optional: fund transfers have neither. */
  statuses?: SelectOption[];
  payments?: SelectOption[];
  onDownload?: () => void;
  children?: ReactNode;
}

/**
 * The filters above every list: RDS `RigoFilters`, one row of chips that wraps only when full, above
 * the table and outside any card (the screen standard, ADR-0093).
 *
 * Every change resets to the first page. Filtering while on page 4 and staying there shows an empty
 * list for a filter that has matches — the single most common way a list screen lies to somebody.
 */
export function ListFilterBar({
  search,
  onChange,
  searchPlaceholder,
  fiscalYears,
  months,
  statuses,
  payments,
  onDownload,
  children,
}: ListFilterBarProps) {
  const set = (patch: Partial<ListSearch>) => onChange({ ...search, ...patch, page: undefined });
  const active = hasActiveFilters(search);

  return (
    <RigoFilters.Root>
      <RigoFilters.StaticRow>
        <div className="w-60">
          <InputText.FilterSearch
            aria-label={searchPlaceholder}
            placeholder={searchPlaceholder}
            value={search.q ?? ""}
            onChange={(event) => set({ q: event.target.value || undefined })}
            onClear={() => set({ q: undefined })}
          />
        </div>

        {statuses ? (
          <FilterChip
            placeholder="Status"
            value={search.status}
            options={statuses.filter((option) => option.value !== "all")}
            onChange={(value) => set({ status: value as ListSearch["status"] })}
          />
        ) : null}

        {payments ? (
          <FilterChip
            placeholder="Payment"
            value={search.payment}
            options={payments.filter((option) => option.value !== "all")}
            onChange={(value) => set({ payment: value as ListSearch["payment"] })}
          />
        ) : null}

        <FilterChip
          placeholder="Fiscal year"
          value={search.fy}
          options={fiscalYears}
          onChange={(value) => set({ fy: value })}
        />

        {/* A month and a hand-typed range are the same question asked two ways, so choosing one
            clears the other rather than filtering by both and returning nothing. */}
        {isCustomRange(search) ? (
          <div className="flex items-center gap-1.5">
            <InputText.Input
              type="date"
              aria-label="From"
              config={{ size: "sm" }}
              className="w-[150px] text-sm"
              value={search.from ?? ""}
              onChange={(event) => set({ from: event.target.value || undefined, month: undefined })}
            />
            <span className="text-muted-foreground text-xs">to</span>
            <InputText.Input
              type="date"
              aria-label="To"
              config={{ size: "sm" }}
              className="w-[150px] text-sm"
              value={search.to ?? ""}
              onChange={(event) => set({ to: event.target.value || undefined, month: undefined })}
            />
          </div>
        ) : (
          <FilterChip
            placeholder="Month"
            value={search.month}
            options={[...months, { value: "custom", label: "Custom range…" }]}
            onChange={(value) =>
              value === "custom"
                ? set({ month: undefined, from: "", to: "" })
                : set({ month: value })
            }
          />
        )}

        <InputText.Input
          type="text"
          inputMode="decimal"
          aria-label="Amount over"
          placeholder="Amount over"
          config={{ size: "sm" }}
          className="w-[120px] text-right text-sm"
          value={search.min ?? ""}
          onChange={(event) => set({ min: event.target.value || undefined })}
        />

        {children}

        {/* Only offered when something is actually filtering. A permanently visible "Clear filters"
            on an unfiltered list is a button that does nothing, and people learn to distrust it. */}
        {active ? (
          <Button
            colorScheme="default"
            variant="ghost"
            size="sm"
            onClick={() => onChange(clearFilters(search))}
          >
            Clear filters
          </Button>
        ) : null}

        {onDownload ? (
          <Button
            colorScheme="default"
            variant="secondary"
            size="sm"
            onClick={onDownload}
            className="ml-auto"
          >
            Download
          </Button>
        ) : null}
      </RigoFilters.StaticRow>
    </RigoFilters.Root>
  );
}

/** One filter, as a chip. It says what it filters by until something is chosen. */
function FilterChip({
  placeholder,
  value,
  options,
  onChange,
}: {
  placeholder: string;
  value?: string;
  options: SelectOption[];
  onChange: (value: string | undefined) => void;
}) {
  return (
    <Filter.SingleSelect
      items={options}
      placeholder={placeholder}
      searchable={options.length > 8}
      icon={<Icon name="Funnel" />}
      value={options.find((option) => option.value === value) ?? null}
      onValueChange={(item) => onChange(item ? String(item.value) : undefined)}
    />
  );
}

/** The page sizes the footer offers, re-exported so a screen imports one module. */
export { PAGE_SIZES };
